import * as React from 'react';
import isEqual from 'react-fast-compare';
import moment from 'moment';
import * as Site from '../../shared/Site';
import { Button, Modal, FormGroup, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { ICalendarItem, ICalendarSettings } from '../calendar/Types';
import { Translate } from '../../shared/LanguageService';
import { IWorkingHour } from '../calendar/IWorkingHour';
import { Calendar } from '../calendar/Calendar';
import { toMinMaxTime } from '../calendar/calendarTimeUtils';
import { assign } from '../../shared/Assign';
import { IPhotographerCalendar } from './Types';
import RadioInputSwitch from '../../components/common/RadioInputSwitch';
import ICalendar from '../calendar/ICalendar';
import WorkingHoursForm from './WorkingHoursForm';
import PhotographerCalendarPrintEvents from './PhotographerCalendarPrintEvents';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { defaultWorkingHours } from './defaultWorkingHours';

interface IState {
    calendar: IPhotographerCalendar;
    calendarBeforeEditingWorkingHours: IPhotographerCalendar;
    calendarSettings: ICalendarSettings;
    showModal: boolean;
    showPrintEventModal: boolean;
    showSe360WorkingHours: boolean;
    showRemoveScandphotosWorkingHoursConfirmation: boolean;
}

interface IProps {
    calendar: any;
    calendarSettings?: {};
    onChange?: (calendar: ICalendar) => void;
    onCreated?: (event: any) => void;
    onUpdated?: (event: any) => void;
    onDeleted?: (event: any) => void;
    onSave?: () => void;
    hideEditWorkingHours?: boolean;
    showSe360WorkingHours?: boolean;
    canAddScandphotosWorkingHours?: boolean;
    isBookableOnScandphotos?: boolean;
    onRangeChanged?: (from: any, to: any) => void;
    dontUseMinMaxTime?: boolean;
    user: any;
    photographerId?: number;
}

class PhotographerCalendarComponent extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.onEventsChange = this.onEventsChange.bind(this);

        let businessHours: any = [0, 1, 2, 3, 4, 5, 6].map((day) => {
            return {
                day: day,
                isAvailable: !(day === 0 || day === 6),
                isAvailableForDusk: day,
                start: moment('08:00:00', 'HH:mm:ss').toDate(),
                end: moment('17:00:00', 'HH:mm:ss').toDate()
            };
        });
        let calendarSettings = this.getCalendarSettings([], businessHours);
        this.state = {
            calendar: {
                workingHours: [],
                items: [],
                photographerIds: [],
                se360WorkingHours: businessHours,
                scandphotosWorkingHours: []
            },
            calendarBeforeEditingWorkingHours: null,
            calendarSettings: calendarSettings,
            showModal: false,
            showPrintEventModal: false,
            showSe360WorkingHours: true,
            showRemoveScandphotosWorkingHoursConfirmation: false
        };

        this.onChange = this.onChange.bind(this);
        this.onEventCreated = this.onEventCreated.bind(this);
        this.onEventUpdated = this.onEventUpdated.bind(this);
        this.onEventDeleted = this.onEventDeleted.bind(this);
        this.workHourDateChange = this.workHourDateChange.bind(this);
        this.showPrintEventModal = this.showPrintEventModal.bind(this);
        this.hidePrintEventModal = this.hidePrintEventModal.bind(this);
    }

    getCalendarSettings(events: any[], workingHours: Array<IWorkingHour>): ICalendarSettings {
        let businessHours = [];

        if (workingHours.length > 0) {
            businessHours = workingHours.map((w) => {
                return {
                    dow: [w.day],
                    start: w.isAvailable ? this.dateToTime(w.start) : '',
                    end: w.isAvailable ? this.dateToTime(w.end) : '',
                    isAvailable: w.isAvailable,
                    isAvailableForDusk: w.isAvailableForDusk
                };
            });
        } else if (this.state && this.state.calendar && this.state.calendar.se360WorkingHours) {
            businessHours = this.state.calendar.se360WorkingHours;
            businessHours = businessHours.map((w) => {
                return {
                    dow: [w.day],
                    start: w.isAvailable ? this.dateToTime(w.start) : '',
                    end: w.isAvailable ? this.dateToTime(w.end) : '',
                    isAvailable: w.isAvailable,
                    isAvailableForDusk: w.isAvailableForDusk
                };
            });
        }

        let calendarSettings: any = {
            selectable: true,
            eventSources: [
                {
                    events: events
                }
            ],
            eventRender: (event, element) => {
                let imageLabel = Translate('photographer/calendar/calendar-item/images');
                let firstProduct =
                    event.product && event.product.includes(',')
                        ? event.product.split(/[,]+/, 1)[0]
                        : event.product;
                let productsArray =
                    event.product && event.product.includes(',') ? event.product.split(/[,]+/) : '';
                if (productsArray.length > 1) {
                    productsArray.shift();
                }
                let additionalProducts = productsArray;

                const calenderItemContent =
                    `<div style="font-size:10px;font-weight: bold;">` +
                    `<span style="font-size:11px">${
                        event.description ? event.description : ''
                    }</span>` +
                    `</br>` +
                    `<span>${firstProduct ? firstProduct : ''}</span>` +
                    (event.productNumberOfImages
                        ? `<span> (</span>` +
                          `<span>${event.productNumberOfImages}</span>` +
                          `<span> ${imageLabel}</span>` +
                          `<span>)</span>` +
                          (additionalProducts
                              ? ` <span>,</span>` + `<span>${additionalProducts}</span>`
                              : '')
                        : '') +
                    `</div>`;

                element.append(calenderItemContent);
            },
            eventConstraint: 'businessHours',
            selectConstraint: 'businessHours',
            selectHelper: true,
            slotLabelFormat: 'HH:mm',
            timeFormat: 'HH:mm',
            firstDay: 1,
            nowIndicator: true,
            header: {
                left: 'prev,next',
                center: 'title',
                right: 'agendaWeek,month'
            },
            defaultView: 'agendaWeek',
            views: {
                agenda: {
                    titleFormat: `${window.innerWidth > 600 ? 'D MMMM YYYY' : 'D/MM'}`
                }
            }
        };

        if (!this.props.dontUseMinMaxTime) {
            const getBusinessHour = (time: string) =>
                time ? parseInt(time.split(':')[0]) : undefined;
            const getMax = (hour: number, previousHour?: number) =>
                Math.max(...[hour === 0 ? 24 : hour + 1, previousHour]);
            const getMin = (hour: number, previousHour?: number) =>
                Math.min(...[hour <= 0 ? 0 : hour - 1, previousHour]);
            let firstAvailableBusinessHour = (businessHours || []).find((x) => x.isAvailable);
            let minTime = firstAvailableBusinessHour
                ? getBusinessHour(firstAvailableBusinessHour.start)
                : 7;
            let maxTime = firstAvailableBusinessHour
                ? getBusinessHour(firstAvailableBusinessHour.end)
                : 18;

            businessHours
                .filter((x) => x.isAvailable)
                .forEach((x) => {
                    minTime = getMin(getBusinessHour(x.start), minTime);
                    maxTime = getMax(getBusinessHour(x.end), maxTime);
                });

            events.forEach((event) => {
                // +/- 1 to get a margin
                const eventStartHour = moment(event.start).hours();
                const eventEndHour = moment(event.end).hours();

                minTime = getMin(eventStartHour, minTime);
                maxTime = getMax(eventEndHour, maxTime);
            });

            const minMaxTime = toMinMaxTime(minTime, maxTime);
            calendarSettings.minTime = minMaxTime.minTime;
            calendarSettings.maxTime = minMaxTime.maxTime;
        }

        if (businessHours.length > 0 || businessHours[0]) {
            calendarSettings.businessHours = businessHours;
        }

        if (this.props.calendarSettings) {
            calendarSettings = { ...calendarSettings, ...this.props.calendarSettings };
        }

        return calendarSettings;
    }

    componentDidMount() {
        if (
            this.props.calendar &&
            Object.prototype.hasOwnProperty.call(this.props.calendar, 'items') &&
            Object.prototype.hasOwnProperty.call(this.props.calendar, 'se360WorkingHours') &&
            Object.prototype.hasOwnProperty.call(this.props.calendar, 'scandphotosWorkingHours')
        ) {
            this.init(assign({}, this.props.calendar));
        }
    }

    componentDidUpdate(previousProps: IProps) {
        let { calendar, showSe360WorkingHours } = this.props;

        if (
            !isEqual(previousProps.calendar, calendar) &&
            calendar &&
            Object.prototype.hasOwnProperty.call(calendar, 'items') &&
            Object.prototype.hasOwnProperty.call(calendar, 'se360WorkingHours') &&
            Object.prototype.hasOwnProperty.call(calendar, 'scandphotosWorkingHours')
        ) {
            this.init(assign({}, calendar));
        }

        if (previousProps.showSe360WorkingHours !== showSe360WorkingHours) {
            this.setState({ showSe360WorkingHours });
        }
    }

    init(calendar: any) {
        calendar.items.forEach((x) => {
            if (x.site === Site.SE360) {
                x.className = ['se360'];
            }
            if (x.site === Site.Scandphotos) {
                x.className = ['scandphotos'];
            }
        });

        let calendarSettings = this.getCalendarSettings(calendar.items, calendar.se360WorkingHours);

        if (calendar.se360WorkingHours.length === 0) {
            calendar.se360WorkingHours = defaultWorkingHours();
        }

        if (
            this.props.calendar.scandphotosWorkingHours &&
            this.props.calendar.scandphotosWorkingHours.length !== 0
        ) {
            if (calendar.scandphotosWorkingHours.length === 0) {
                calendar.scandphotosWorkingHours = defaultWorkingHours();
            }
        }

        let showSe360WorkingHours = this.state.showSe360WorkingHours;
        if (
            this.props.showSe360WorkingHours === undefined &&
            calendar.scandphotosWorkingHours.length === 0
        ) {
            showSe360WorkingHours = true;
        }

        this.setState({
            calendarSettings: calendarSettings,
            calendar: calendar,
            showSe360WorkingHours: showSe360WorkingHours
        } as IState);
    }

    showModal() {
        this.setState({
            showModal: true,
            calendarBeforeEditingWorkingHours: this.state.calendar
        } as IState);
    }

    showPrintEventModal() {
        this.setState({ showPrintEventModal: true } as IState);
    }

    dateToTime(str: string): string {
        let d = null;

        if (moment.isMoment(str)) {
            d = moment(str).toDate();
        } else {
            d = new Date(str);
        }

        if (!isNaN(d.getTime())) {
            let hhmmss = d.toTimeString().split(' ')[0];
            let temp = hhmmss.split(':');
            return `${temp[0]}:${temp[1]}`;
        }
        return str;
    }

    hideModal() {
        this.setState({ showModal: false } as IState);
    }

    hidePrintEventModal() {
        this.setState({ showPrintEventModal: false } as IState);
    }

    discardWorkingHoursChanges() {
        this.hideModal();
        this.onChange(this.state.calendarBeforeEditingWorkingHours);
    }

    hasAddedUnsavedScandphotosWorkingHours() {
        let calendarBeforeEditing = this.state.calendarBeforeEditingWorkingHours;
        return (
            this.props.canAddScandphotosWorkingHours &&
            calendarBeforeEditing !== null &&
            calendarBeforeEditing.scandphotosWorkingHours.length === 0
        );
    }

    saveBusinessHours() {
        this.hideModal();
        if (typeof this.props.onSave === 'function') {
            this.props.onSave();
        }
    }

    onChange(calendar: IPhotographerCalendar) {
        if (this.props.onChange) {
            this.props.onChange(calendar);
        }
    }

    onEventCreated(event: any) {
        event.photographerId = this.props.calendar.photographerId;
        if (this.props.onCreated) {
            this.props.onCreated(event);
        }
    }

    onEventUpdated(event: any) {
        if (this.props.onUpdated) {
            this.props.onUpdated(event);
        }
    }

    onEventDeleted(event: any) {
        if (this.props.onDeleted) {
            this.props.onDeleted(event);
        }
    }

    onEventsChange(events: ICalendarItem[]) {
        let calendar: IPhotographerCalendar = assign({}, this.state.calendar);
        calendar.items = assign([], events);
        this.setState({ calendar: calendar } as IState);

        this.onChange(calendar);
    }

    workHourDateChange(workingHours: any[], isSe360: boolean) {
        let calendar = assign({}, this.state.calendar);

        if (isSe360) {
            calendar.se360WorkingHours = assign([], workingHours);
        } else {
            calendar.scandphotosWorkingHours = assign([], workingHours);
        }

        this.onChange(calendar);
    }

    addScandphotosWorkingHours() {
        this.workHourDateChange(defaultWorkingHours(), false);
    }

    removeScandphotosWorkingHours() {
        this.workHourDateChange([], false);
    }

    requestRemovingScandphotosWorkingHours() {
        if (this.props.isBookableOnScandphotos) {
            this.setState({ showRemoveScandphotosWorkingHoursConfirmation: true } as IState);
        } else {
            this.removeScandphotosWorkingHours();
        }
    }

    cloneWorkingHoursArray(workingHoursList: any[]) {
        let workingHours = [];
        let sunday: any = null;
        for (let i = 0; i < workingHoursList.length; i++) {
            let workingHour = workingHoursList[i];
            let wh = {
                day: workingHour.day,
                start: moment(workingHour.start),
                end: moment(workingHour.end),
                isAvailable: workingHour.isAvailable,
                isAvailableForDusk: workingHour.isAvailableForDusk
            };
            if (workingHour.day === 0) {
                sunday = wh;
            } else {
                workingHours.push(wh);
            }
        }

        workingHours = workingHours.sort((a, b) => {
            if (b.day === 0) return -1;
            return a.day > b.day ? 1 : a.day === b.day ? 0 : -1;
        });

        if (sunday !== null) {
            workingHours.push(sunday);
        }

        return workingHours;
    }

    showDirections = async (itemsOfTheDay: any) => {
        const weekDay = () => {
            return new Date(itemsOfTheDay[0].start).getDay();
        };
        const photographerAddress =
            this.state.calendar.se360WorkingHours[weekDay()].coordinates;

        const eventAddresses = [];

        const directionsLink = `https://www.google.com/maps/dir/?api=1&origin=${photographerAddress}&destination=${photographerAddress}&travelmode=driving`;

        for (const item of itemsOfTheDay) {
            eventAddresses.push(item.coordinates);
        }

        eventAddresses.length &&
            window.open(directionsLink + '&waypoints=' + eventAddresses.join('|'), '_blank');
    };

    render() {
        let se360WorkingHours = this.cloneWorkingHoursArray(this.state.calendar.se360WorkingHours);
        let scandphotosWorkingHours = this.cloneWorkingHoursArray(
            this.state.calendar.scandphotosWorkingHours
        );

        return (
            <div>
                {this.props.hideEditWorkingHours !== true ? (
                    <Modal
                        show={this.state.showModal}
                        onHide={() => {
                            this.discardWorkingHoursChanges();
                        }}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {Translate('photographer/modal/form/calendar/edit-working-hours')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Tabs defaultActiveKey={1} id="working-hours-uncontrolled-tab">
                                <Tab eventKey={1} title="SE360">
                                    <WorkingHoursForm
                                        workingHours={se360WorkingHours}
                                        onChange={(workingHours: any[]) => {
                                            this.workHourDateChange(workingHours, true);
                                        }}
                                        user={this.props.user}
                                    />
                                </Tab>
                                {this.props.calendar.scandphotosWorkingHours &&
                                    this.props.calendar.scandphotosWorkingHours.length !== 0 ? (
                                    <Tab eventKey={2} title="Scandphotos">
                                        <WorkingHoursForm
                                            user={this.props.user}
                                            workingHours={scandphotosWorkingHours}
                                            onChange={(workingHours: any[]) => {
                                                this.workHourDateChange(workingHours, false);
                                            }}
                                        />
                                        {this.hasAddedUnsavedScandphotosWorkingHours() ? (
                                            <Button
                                                onClick={() => {
                                                    this.removeScandphotosWorkingHours();
                                                }}
                                            >
                                                {Translate('cancel')}
                                            </Button>
                                        ) : this.props.canAddScandphotosWorkingHours ? (
                                            <Button
                                                bsStyle="danger"
                                                onClick={() => {
                                                    this.requestRemovingScandphotosWorkingHours();
                                                }}
                                            >
                                                {Translate(
                                                    'photographer/modal/form/calendar/remove-scandphotos-working-hours'
                                                )}
                                            </Button>
                                        ) : null}
                                    </Tab>
                                ) : this.props.canAddScandphotosWorkingHours ? (
                                    <Tab eventKey={2} title="Scandphotos">
                                        <Button
                                            bsStyle="info"
                                            onClick={() => {
                                                this.addScandphotosWorkingHours();
                                            }}
                                        >
                                            {Translate(
                                                'photographer/modal/form/calendar/add-scandphotos-working-hours'
                                            )}
                                        </Button>
                                    </Tab>
                                ) : null}
                            </Tabs>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onClick={() => {
                                    this.saveBusinessHours();
                                }}
                            >
                                {Translate('photographers/calendar/page/button-label/save')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                ) : null}
                {this.state.showRemoveScandphotosWorkingHoursConfirmation ? (
                    <ConfirmationModal
                        title={Translate(
                            'photographer/modal/form/calendar/remove-scandphotos-working-hours/confirmation/title'
                        )}
                        text={Translate(
                            'photographer/modal/form/calendar/remove-scandphotos-working-hours/confirmation/text'
                        )}
                        doButtonLabel={Translate(
                            'photographer/modal/form/calendar/remove-scandphotos-working-hours/confirmation/confirm'
                        )}
                        doButtonStyle="danger"
                        cancelButtonLabel={Translate('cancel')}
                        do={() => {
                            this.removeScandphotosWorkingHours();
                        }}
                        closeModal={() => {
                            this.setState({
                                showRemoveScandphotosWorkingHoursConfirmation: false
                            } as IState);
                        }}
                    />
                ) : null}
                {this.state.showPrintEventModal ? (
                    <div>
                        <PhotographerCalendarPrintEvents
                            show={this.state.showPrintEventModal}
                            hide={() => {
                                this.hidePrintEventModal();
                            }}
                            photographerId={this.props.photographerId}
                        />
                    </div>
                ) : null}
                <Row className="row-photographer-calendar-toolbar">
                    <Col
                        xs={6}
                        className="photographer-calendar-toolbar photographer-calendar-toolbar-left"
                    >
                        {this.props.showSe360WorkingHours === undefined &&
                            this.props.calendar.scandphotosWorkingHours &&
                            this.props.calendar.scandphotosWorkingHours.length !== 0 ? (
                            <FormGroup className="radio-switch radio-switch-left">
                                <RadioInputSwitch
                                    name="se360"
                                    id="se360"
                                    checked={this.state.showSe360WorkingHours === true}
                                    onChange={() => {
                                        this.setState({
                                            showSe360WorkingHours: true
                                        });
                                    }}
                                    label="SE360"
                                />
                                <RadioInputSwitch
                                    name="scandphotos"
                                    id="scandphotos"
                                    checked={this.state.showSe360WorkingHours !== true}
                                    onChange={() => {
                                        this.setState({
                                            showSe360WorkingHours: false
                                        });
                                    }}
                                    label="Scandphotos"
                                />
                            </FormGroup>
                        ) : null}
                    </Col>
                    <Col
                        xs={6}
                        className="photographer-calendar-toolbar photographer-calendar-toolbar-right"
                    >
                        {this.props.hideEditWorkingHours !== true ? (
                            <Button
                                className="edit-working-hours"
                                style={{ marginRight: '1rem', height: '34px' }}
                                bsStyle="info"
                                onClick={() => {
                                    this.showModal();
                                }}
                            >
                                {Translate('photographer/modal/form/calendar/edit-working-hours')}
                            </Button>
                        ) : null}
                        <Button
                            className="photographer-calendar print-order"
                            style={{ height: '34px', marginRight: 0 }}
                            onClick={() =>
                                this.setState({
                                    showPrintEventModal: true
                                } as IState)
                            }
                        >
                            {Translate('photographer/modal/form/calendar/print')}
                        </Button>
                    </Col>
                </Row>
                <Calendar
                    settings={this.state.calendarSettings}
                    calendarItems={this.state.calendar ? this.state.calendar.items : null}
                    workingHours={
                        this.state.showSe360WorkingHours
                            ? this.state.calendar.se360WorkingHours
                            : this.state.calendar.scandphotosWorkingHours
                    }
                    onChange={this.onEventsChange}
                    onUpdated={this.onEventUpdated}
                    onCreated={this.onEventCreated}
                    onDeleted={this.onEventDeleted}
                    onRangeChanged={this.props.onRangeChanged}
                    onPersonalDirectionsClick={this.showDirections}
                    user={this.props.user}
                />
            </div>
        );
    }
}

export default PhotographerCalendarComponent;
